/**
 * LA VENTANA PARA INSTALAR LA PÁGINA
 * =============================================================================
 * Ofrece instalar la web en el teléfono y la instala de verdad.
 *
 * -----------------------------------------------------------------------------
 * LOS DOS CAMINOS, Y POR QUÉ SON DISTINTOS
 * -----------------------------------------------------------------------------
 *   · ANDROID Y CHROME
 *     El navegador avisa cuando la página se puede instalar, con un evento que se
 *     llama `beforeinstallprompt`. Ese aviso se puede guardar y disparar después
 *     desde código. Así el botón instala en un toque, sin salir de la página.
 *
 *   · IPHONE Y SAFARI
 *     Ese evento NO EXISTE. Apple no deja que una página instale sola: hay que
 *     hacerlo a mano desde el menú Compartir. Aquí no hay nada que disparar, así
 *     que se explican los tres pasos con el nombre exacto del botón.
 *
 * -----------------------------------------------------------------------------
 * CUÁNDO APARECE Y CUÁNDO NO
 * -----------------------------------------------------------------------------
 *   · NO aparece si la página ya está instalada. Se detecta en el modo en el que
 *     se está mostrando: si es `standalone` o `fullscreen`, ya está instalada.
 *   · NO vuelve a aparecer si se cerró antes. Se acuerda en el navegador. Insistir
 *     con una ventana que alguien ya cerró es la forma más rápida de que odie la
 *     página.
 *   · NO aparece en la computadora. Ahí no tiene sentido y solo estorba.
 *   · SÍ aparece solo, a los pocos segundos, la primera vez en un teléfono. Y
 *     también se puede abrir cuando se quiera con la pastilla de abajo.
 */

import { useCallback, useEffect, useState } from 'react';
import { Icono } from './Iconos';
import { Logo } from './Logo';
import { useIdioma } from '../i18n/useIdioma';

/**
 * El aviso del navegador. No está en los tipos de TypeScript porque es una cosa
 * que solo existe en Chrome y en algunos navegadores de Android.
 */
interface AvisoDeInstalacion extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/** Se acuerda de que ya se cerró, para no insistir. */
const CLAVE = 'msoccer-instalar-cerrado';

/** Si la página ya se está mostrando como aplicación. */
function yaEstaInstalada(): boolean {
  if (typeof window === 'undefined') return false;

  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: fullscreen)').matches ||
    // Safari en iPhone lo dice con una propiedad propia.
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

/** Si es un teléfono. En la computadora no se ofrece. */
function esTelefono(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent);
}

/** Si es un iPhone o un iPad, que necesitan los pasos a mano. */
function esApple(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

export function Instalar() {
  const { textos } = useIdioma();
  const t = textos.instalar;

  const [abierta, setAbierta] = useState(false);
  const [aviso, setAviso] = useState<AvisoDeInstalacion | null>(null);
  const [instalada, setInstalada] = useState(false);
  /** Si se puede ofrecer. Se calcula una vez, al montar. */
  const [sePuedeOfrecer, setSePuedeOfrecer] = useState(false);

  useEffect(() => {
    // Ya está instalada, o es una computadora, o ya se cerró antes: no se ofrece.
    if (yaEstaInstalada() || !esTelefono()) return;

    try {
      if (localStorage.getItem(CLAVE) === 'si') return;
    } catch {
      // Sin permiso para leer: se ofrece igual, no es grave.
    }

    setSePuedeOfrecer(true);

    /* --- El aviso del navegador, cuando llegue --- */
    const alPoderInstalar = (e: Event) => {
      // Sin esto, el navegador muestra su propia barra. Se prefiere la ventana
      // propia, que va con el diseño de la página.
      e.preventDefault();
      setAviso(e as AvisoDeInstalacion);
    };

    const alInstalar = () => {
      setInstalada(true);
      setAbierta(false);
      try {
        localStorage.setItem(CLAVE, 'si');
      } catch {
        /* sin permiso, da igual */
      }
    };

    window.addEventListener('beforeinstallprompt', alPoderInstalar);
    window.addEventListener('appinstalled', alInstalar);

    /* --- Y se ofrece sola, a los pocos segundos --- */
    // El retardo es a propósito: si la ventana salta nada más entrar, antes de que
    // se vea la página, molesta. A los seis segundos ya se vio de qué va.
    const temporizador = window.setTimeout(() => setAbierta(true), 6000);

    return () => {
      window.removeEventListener('beforeinstallprompt', alPoderInstalar);
      window.removeEventListener('appinstalled', alInstalar);
      window.clearTimeout(temporizador);
    };
  }, []);

  /** Cierra la ventana y se acuerda, para no volver a ofrecerla. */
  const cerrar = useCallback(() => {
    setAbierta(false);
    try {
      localStorage.setItem(CLAVE, 'si');
    } catch {
      /* sin permiso, da igual */
    }
  }, []);

  /** Instala de verdad, cuando el navegador lo permite. */
  const instalar = useCallback(async () => {
    if (!aviso) return;

    await aviso.prompt();
    const eleccion = await aviso.userChoice;

    if (eleccion.outcome === 'accepted') {
      setInstalada(true);
      setAbierta(false);
    } else {
      // Dijo que no en el aviso del navegador: se cierra y no se insiste.
      cerrar();
    }

    // El aviso solo sirve una vez.
    setAviso(null);
  }, [aviso, cerrar]);

  // ---------------------------------------------------------------------------
  // La pastilla para abrirla cuando se quiera
  // ---------------------------------------------------------------------------
  // Se pone siempre que se pueda ofrecer, aunque la ventana se haya cerrado: así
  // quien la cerró por error la puede volver a encontrar sin tener que buscar en
  // el menú del navegador.
  if (sePuedeOfrecer && !abierta) {
    return (
      <button
        type="button"
        className="instalar-pastilla"
        onClick={() => setAbierta(true)}
        aria-label={t.titulo}
      >
        <Icono nombre="descargar" tam={18} />
        <span>{t.boton}</span>
      </button>
    );
  }

  if (!abierta) return null;

  // ---------------------------------------------------------------------------
  // La ventana
  // ---------------------------------------------------------------------------
  const esIphone = esApple();

  return (
    <div className="instalar" role="dialog" aria-modal="true" aria-labelledby="instalar-titulo">
      {/* Al tocar fuera se cierra. Es lo que espera cualquiera. */}
      <button
        type="button"
        className="instalar__fondo"
        onClick={cerrar}
        aria-label={t.cerrar}
        tabIndex={-1}
      />

      <div className="instalar__caja">
        <button
          type="button"
          className="instalar__cerrar"
          onClick={cerrar}
          aria-label={t.cerrar}
        >
          <Icono nombre="cerrar" tam={20} />
        </button>

        <div className="instalar__marca">
          <Logo alto={46} conTexto />
        </div>

        {instalada ? (
          <>
            <p className="instalar__titulo" id="instalar-titulo">
              {t.instalada}
            </p>
            <button type="button" className="boton boton--oro" onClick={cerrar}>
              {t.cerrar}
            </button>
          </>
        ) : (
          <>
            <h2 className="instalar__titulo" id="instalar-titulo">
              {esIphone && !aviso ? t.tituloIphone : t.titulo}
            </h2>

            <p className="instalar__texto">{t.texto}</p>

            {/* Las tres ventajas, con su palomita. */}
            <ul className="instalar__ventajas">
              {t.ventajas.map((v) => (
                <li key={v}>
                  <Icono nombre="palomita" tam={17} />
                  <span>{v}</span>
                </li>
              ))}
            </ul>

            {/* Si el navegador deja instalar, se instala. Si no —iPhone—, se
                explican los pasos. */}
            {aviso ? (
              <div className="instalar__acciones">
                <button type="button" className="boton boton--oro" onClick={instalar}>
                  <span>{t.boton}</span>
                  <Icono nombre="descargar" tam={17} />
                </button>
                <button type="button" className="boton boton--marco" onClick={cerrar}>
                  {t.ahoraNo}
                </button>
              </div>
            ) : (
              <>
                {esIphone && (
                  <ol className="instalar__pasos">
                    {t.pasosIphone.map((p) => (
                      <li key={p}>{p}</li>
                    ))}
                  </ol>
                )}

                {!esIphone && (
                  <>
                    <p className="instalar__subtitulo">{t.tituloAndroid}</p>
                    <ol className="instalar__pasos">
                      {t.pasosAndroid.map((p) => (
                        <li key={p}>{p}</li>
                      ))}
                    </ol>
                  </>
                )}

                <div className="instalar__acciones">
                  <button type="button" className="boton boton--marco" onClick={cerrar}>
                    {t.ahoraNo}
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
